import { Button, Stack, Typography } from '@mui/material'
import { Feature } from 'api/model'
import { COLORS } from 'app-constants'
import JelledModal from 'components/JelledModal'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import { ReactComponent as Graphic } from './topic-label-graphic.svg'
import SetupPermissionsModal from 'components/Permissions/SetupPermissionsModal'
import { useMemo, useState } from 'react'
import { PermissionFeature } from 'state/permission'

function hexToRgba(hex: string, opacity: string) {
  let r = 0,
    g = 0,
    b = 0

  // 3 digits
  if (hex.length === 4) {
    r = parseInt(hex[1] + hex[1], 16)
    g = parseInt(hex[2] + hex[2], 16)
    b = parseInt(hex[3] + hex[3], 16)
  }
  // 6 digits
  else if (hex.length === 7) {
    r = parseInt(hex[1] + hex[2], 16)
    g = parseInt(hex[3] + hex[4], 16)
    b = parseInt(hex[5] + hex[6], 16)
  }

  return `rgba(${r}, ${g}, ${b}, ${opacity})`
}

// informatory modal describing the usage of topics for organizing emails
export const TopicLabelModal = ({
  advancedSettingsFeaturePermissions,
  onClose,
}: {
  advancedSettingsFeaturePermissions?: PermissionFeature | null
  onClose: () => void
}) => {
  const isMobile = useCheckMobileScreen()

  const hasGrantedAdvancedPermissions = advancedSettingsFeaturePermissions?.missingPermissions.length === 0

  const acceptedPermissions = useMemo(() => {
    return advancedSettingsFeaturePermissions?.acceptedPermissions ?? []
  }, [advancedSettingsFeaturePermissions])
  const missingPermissions = useMemo(() => {
    return advancedSettingsFeaturePermissions?.missingPermissions ?? []
  }, [advancedSettingsFeaturePermissions])

  // permissions state
  const [isPermissionsModalOpen, setIsPermissionsModalOpen] = useState<boolean>(false)
  const onClosePermissionsModal = () => {
    setIsPermissionsModalOpen(false)
  }

  const renderBodyText = () => {
    if (hasGrantedAdvancedPermissions) {
      return 'Apply topics as labels in Gmail so you can easily get a grasp of your inbox with just a glance. You can change this in Settings < Emails.'
    }
    return 'Apply topics as labels in Gmail so you can easily get a grasp of your inbox with just a glance. Additional permissions are required to use this feature.'
  }

  // depending on whether advanced permissions are granted, we may render different buttons
  const renderButtons = () => {
    if (hasGrantedAdvancedPermissions) {
      return (
        <Button
          color="primary"
          variant="contained"
          onClick={() => {
            onClose()
          }}
          sx={{
            my: 3,
            width: 'fit-content',
            alignSelf: 'center',
          }}>
          Continue
        </Button>
      )
    }
    return (
      <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={2} mt="42px" px={4}>
        <Button
          color="primary"
          variant="text"
          onClick={() => {
            onClose()
          }}>
          Cancel
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            // should trigger showing the grant permissions modal for advanced settings
            setIsPermissionsModalOpen(true)
          }}>
          Grant permissions
        </Button>
      </Stack>
    )
  }

  return (
    <JelledModal
      onClose={onClose}
      isOpen={true}
      width={isMobile ? 'calc(100% - 32px)' : '702px'}
      modalStyle={{
        backgroundColor: COLORS.PURE_WHITE,
        paddingTop: 0,
        paddingLeft: 0,
        paddingRight: 0,
        height: isMobile ? '100%' : '793px',
      }}>
      <Stack justifyContent="center" alignItems="center">
        {
          <Graphic
            style={{
              width: isMobile ? '100%' : '702px',
            }}
          />
        }
      </Stack>

      <Stack spacing={3} justifyContent="center" maxWidth={isMobile ? '100%' : '518px'} alignSelf="center" mt={3}>
        <Stack
          p="4px 8px"
          borderRadius="4px"
          sx={{
            justifyContent: 'center',
            alignItems: 'center',
          }}>
          <Stack
            p="4px 8px"
            justifyContent="center"
            alignItems="center"
            borderRadius="4px"
            sx={{
              background: COLORS.JELLED_PRIMARY_BLUE,
            }}>
            <Typography
              variant="h7.5"
              sx={{
                color: COLORS.PURE_WHITE,
              }}>
              NEW!
            </Typography>
          </Stack>
        </Stack>

        <Typography variant="h3" textAlign="center">
          Use topics to organize your emails
        </Typography>

        <Typography
          variant="pS"
          textAlign="center"
          sx={{
            color: COLORS.GRAY_2,
          }}>
          {renderBodyText()}
        </Typography>
      </Stack>
      {renderButtons()}
      {isPermissionsModalOpen && (
        <SetupPermissionsModal
          additionalParams={{
            isSettingsModalOpen: 'true', // used to open the settings modal after permissions are granted
            isActivateEmailResponderActive: 'true', // used to turn the toggle on after permissions are granted
            isEnabledUseInsightTopicsAsLabels: 'true', // turns toggle on for using topics as labels
          }}
          onClose={onClosePermissionsModal}
          acceptedPermissions={acceptedPermissions}
          missingPermissions={missingPermissions}
          feature={Feature.advanced_email_responder}
        />
      )}
    </JelledModal>
  )
}

const TopicLabel = ({ text, color }: { text: string; color: string }) => {
  return (
    <Stack
      borderRadius="6px"
      padding="4px 12px"
      direction="row"
      alignItems="center"
      spacing={1}
      width="fit-content"
      sx={{
        backgroundColor: hexToRgba(color, '0.5'),
      }}>
      <Stack
        borderRadius="3px"
        height="12px"
        width="12px"
        border="1px solid #fff"
        sx={{
          backgroundColor: color,
        }}
      />
      <Typography variant="h5">{text}</Typography>
    </Stack>
  )
}

export default TopicLabel
